'use client';

import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarGroup,
  SidebarGroupLabel,
  useSidebar,
} from '../ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { ChevronRight } from 'lucide-react';
import { Fingerprint } from 'lucide-react';
import { Moon, Sun } from "lucide-react"
import { useTheme } from '../themeProvider';
import { ImagePaths } from '../../utils/ImagePaths';
import appLogo from "../../assets/svg/appLogo.svg";

/**
 * Does `pathname` belong to `href`?
 *
 * Exact match, or a child segment. A bare startsWith would make
 * /reports/lead-sources count as a match for /reports/lead-source, quietly
 * lighting up the wrong menu entry.
 */
const matchesRoute = (pathname, href) =>
  !!href && (pathname === href || pathname.startsWith(href + '/'));

export function SiteSidebar({ menuItems }) {
  const location = useLocation();
  const { state: sidebarState, isMobile } = useSidebar();
  const [openSubmenus, setOpenSubmenus] = useState({});
  const { theme, setTheme, mounted } = useTheme();

  const isCollapsed = sidebarState === 'collapsed' && !isMobile;

  useEffect(() => {
    if (isCollapsed) {
      setOpenSubmenus({});
      return;
    }

    const newOpenState = {};
    let matched = false;
    for (const item of menuItems) {
      if (item.subItems && item.subItems.some(sub => matchesRoute(location.pathname, sub.href))) {
        newOpenState[item.label] = true;
        matched = true;
      }
    }
    // Reports used to be pinned open unconditionally. With a second reports
    // menu that meant picking an Ad Report expanded Reports as well, so two
    // menus of submenus fought for the same space. It is now only the fallback
    // for a route that belongs to no submenu at all.
    if (!matched && menuItems.some(i => i.label === 'Reports')) {
      newOpenState['Reports'] = true;
    }
    setOpenSubmenus(newOpenState);
    // menuItems is deliberately NOT a dependency. useNavigation() rebuilds the
    // array on every render, so including it re-ran this effect after every
    // state change — including the setOpenSubmenus fired by clicking a menu.
    // The effect then recomputed from the URL alone and closed the submenu the
    // user had just opened, which made any menu whose trigger only toggles
    // (rather than navigating) impossible to keep open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isCollapsed]);

  const toggleSubmenu = (label, isOpen) => {
    if (isOpen) {
      setOpenSubmenus({ [label]: true });
    } else {
      setOpenSubmenus(prev => ({ ...prev, [label]: false }));
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const mainNav = menuItems.filter(item => !item.isSecondary);
  const secondaryNav = menuItems.filter(item => item.isSecondary);

  const renderNavItems = (items) => {
    return items.map((item) => {
      const isActive = item.subItems
        ? item.subItems.some(sub => matchesRoute(location.pathname, sub.href))
        : (item.href ? location.pathname.startsWith(item.href) &&
            (item.href !== '/dashboard' || location.pathname === '/dashboard') &&
            !(item.excludePrefixes || []).some(p => location.pathname.startsWith(p)) : false);
      const isSubmenuOpen = openSubmenus[item.label] || false;

      if (item.subItems && isCollapsed) {
        return (
          <SidebarMenuItem key={item.label}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="relative">
                  <SidebarMenuButton
                    isActive={isActive}
                    tooltip={item.label}
                    className="w-full justify-center"
                  >
                    {item.icon && <item.icon />}
                    <span className="sr-only">{item.label}</span>
                  </SidebarMenuButton>
                  {/* {isActive && <div className="absolute left-1 top-1/2 -translate-y-1/2 h-6 w-1 bg-primary rounded-r-lg" />} */}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start" sideOffset={10}>
                <DropdownMenuItem asChild className='hover:bg-white cursor-default'>
                  <span className="px-2 py-1 font-semibold text-base">{item.label}</span>
                </DropdownMenuItem>
                {item.subItems.map((subItem) => (
                  <DropdownMenuItem className='py-2' key={subItem.label} asChild isActive={location.pathname === subItem.href}>
                    <Link to={subItem.href} className="flex items-center gap-2 cursor-pointer w-full">
                      {subItem.icon && <subItem.icon className="h-4 w-4" />}
                      <span>{subItem.label}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        );
      }

      return item.subItems ? (
        <SidebarMenuItem key={item.label}>
          <Collapsible
            open={isSubmenuOpen}
            onOpenChange={(isOpen) => toggleSubmenu(item.label, isOpen)}
          >
            <CollapsibleTrigger asChild>
              <div className="relative">
                {/* Same structure as a plain link below (icon, label, then the
                    chevron pushed right) so both kinds of row share one icon
                    size, one gap and one label start. */}
                <SidebarMenuButton
                  className="w-full"
                  isActive={isActive}
                  tooltip={item.label}
                >
                  {item.icon && <item.icon />}
                  <span className="flex-1 truncate group-data-[collapsible=icon]:hidden">{item.label}</span>
                  <ChevronRight
                    className="ml-auto !size-4 shrink-0 opacity-70 transition-transform duration-300 group-data-[collapsible=icon]:hidden data-[state=open]:rotate-90"
                    data-state={isSubmenuOpen ? 'open' : 'closed'}
                  />
                </SidebarMenuButton>
                {/* {isActive && <div className="absolute left-1 top-1/2 -translate-y-1/2 h-6 w-1 bg-primary rounded-r-lg" />} */}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent asChild>
              <SidebarMenuSub data-state={openSubmenus[item.label] ? 'open' : 'closed'} className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                {item.subItems.map((subItem) => (
                  <SidebarMenuSubItem key={subItem.label}>
                    <SidebarMenuSubButton asChild isActive={location.pathname === subItem.href}>
                      <Link to={subItem.href}>
                        <span>{subItem.label}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </Collapsible>
        </SidebarMenuItem>
      ) : (
        <SidebarMenuItem key={item.label}>
          <div className="relative">
            <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
              <Link to={item.href}>
                {item.icon && <item.icon />}
                <span className="truncate group-data-[collapsible=icon]:hidden">{item.label}</span>
              </Link>
            </SidebarMenuButton>
            {/* {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 bg-primary rounded-r-lg group-data-[collapsible=icon]:left-0" />} */}
          </div>
        </SidebarMenuItem>
      );
    });
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2">
          <div className="flex justify-center">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center bg-gradient-to-br from-[var(--brand-from)] to-[var(--brand-to)] shadow-[0_8px_20px_-8px_var(--brand-glow)]">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-brand-950 dark:text-white group-data-[collapsible=icon]:hidden">
                Gro Wing CRM
              </h1>
            </div>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">Main Menu</SidebarGroupLabel>
          <SidebarMenu>
            {renderNavItems(mainNav)}
          </SidebarMenu>
        </SidebarGroup>
        {secondaryNav.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">Tools & Settings</SidebarGroupLabel>
            <SidebarMenu>
              {renderNavItems(secondaryNav)}
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        {mounted && (
          <div className="flex items-center gap-2 group-data-[collapsible=icon]:flex-col cursor-pointer" onClick={toggleTheme}>
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 group-data-[collapsible=icon]:w-full"
            >
              <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0 text-foreground" />
              <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100 text-foreground" />
              <span className="sr-only">Toggle theme</span>
            </Button>
            <div className="group-data-[collapsible=icon]:hidden text-sm text-sidebar-foreground">
              {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
            </div>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}